import { InlineHelp } from "@/components/common/InlineHelp";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";

export type BuildPipelineView = "kanban" | "roster" | "phaseSettings";

const TABS: ReadonlyArray<{ id: BuildPipelineView; label: string }> = [
  { id: "kanban", label: "Plant Kanban" },
  { id: "roster", label: "Units" },
  { id: "phaseSettings", label: "Phase settings" },
];

const KANBAN_HELP =
  "Drag cards between columns or use move buttons in the unit drawer. Leaving the initial phase issues a plant serial; entering Build complete records the COGS production-dollar event. Moves respect phase sub-role tags. Open a unit for BOM, issuance, and history.";

const ROSTER_HELP =
  "Flat unit list and create flow. Each shed keeps a permanent UUID; issue serial / mark build complete here or move cards on Plant Kanban. Filter by plant or sale path.";

const PHASE_SETTINGS_HELP =
  "Org-wide Kanban columns. phase_key is fixed after create. Exactly one Initial and one Build complete phase are required.";

export function ManufacturingBuildPipelineViewToggle({
  view,
  onChange,
  showPhaseSettings,
  productionScoped,
}: {
  view: BuildPipelineView;
  onChange: (next: BuildPipelineView) => void;
  showPhaseSettings: boolean;
  productionScoped: boolean;
}) {
  const tabs = showPhaseSettings ? TABS : TABS.filter((t) => t.id !== "phaseSettings");
  const help =
    view === "phaseSettings"
      ? PHASE_SETTINGS_HELP
      : view === "roster"
        ? ROSTER_HELP
        : productionScoped
          ? `${KANBAN_HELP} Plant managers see units already in production (serial issued) for their assigned plants.`
          : KANBAN_HELP;

  const helpLabel =
    view === "phaseSettings"
      ? "Phase settings help"
      : view === "roster"
        ? "Units roster help"
        : "Plant Kanban help";

  return (
    <div className="flex flex-wrap items-center gap-2">
      {tabs.length > 1 ? (
        <div
          role="tablist"
          aria-label="Build pipeline view"
          className="inline-flex flex-nowrap overflow-x-auto rounded-lg bg-gray-100 p-0.5 text-sm dark:bg-zinc-800"
        >
          {tabs.map((tab) => {
            const active = tab.id === view;
            const fireflyId =
              tab.id === "roster"
                ? "mfg-build-pipeline-units-tab"
                : tab.id === "kanban"
                  ? "mfg-build-pipeline-kanban-tab"
                  : undefined;
            return (
              <button
                key={tab.id}
                role="tab"
                aria-selected={active}
                type="button"
                onClick={() => onChange(tab.id)}
                {...(fireflyId ? { [FIREFLY_TARGET_ATTR]: fireflyId } : {})}
                className={
                  "shrink-0 whitespace-nowrap rounded-md px-3 py-1 text-sm font-semibold transition-colors " +
                  (active
                    ? "bg-white text-gray-900 shadow-sm dark:bg-zinc-900 dark:text-zinc-100"
                    : "text-gray-600 hover:text-gray-900 dark:text-zinc-400 dark:hover:text-zinc-200")
                }
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      ) : (
        <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Plant Kanban</h3>
      )}
      <InlineHelp label={helpLabel} glyph="!">
        {help}
      </InlineHelp>
    </div>
  );
}
